import { useState, useEffect, useRef } from 'react';
import { validateImageUrl } from '../utils/imageHelper';

interface UseImageWithFallbackOptions {
  fallbackUrl?: string;
  timeoutMs?: number;
  retries?: number;
}

/**
 * Hook to validate and load images with automatic retry and fallback support.
 * 
 * Features:
 * - Automatic retry with exponential backoff (500ms, 1s, 2s, 4s, 8s, 16s, capped at 30s)
 * - Infinite retries by default - will keep trying until image loads or component unmounts
 * - Configurable timeout per attempt
 * - Loading state during validation and retries
 * - Error state tracking
 * - Automatic cleanup on unmount (AbortController)
 * 
 * @param imageUrl - The image URL to validate and load
 * @param options - Configuration options
 * @param options.fallbackUrl - URL to use if validation fails (default: '/fallback.png')d q
 * @param options.timeoutMs - Timeout for each attempt in milliseconds (default: 5000)
 * @param options.retries - Number of retry attempts (default: Infinity for infinite retries)
 * @returns Object with validated imageUrl, loading state, and error state
 */
export const useImageWithFallback = (
  imageUrl: string | null | undefined,
  options: UseImageWithFallbackOptions = {}
) => {
  const {
    fallbackUrl = '/fallback.png',
    timeoutMs = 5000,
    retries = Infinity
  } = options;

  const [validatedImageUrl, setValidatedImageUrl] = useState<string>(fallbackUrl);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  
  // Use ref to track if component is mounted
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Create new AbortController for this effect
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    
    const loadImage = async () => {
      setIsLoading(true);
      setHasError(false);
      
      try {
        const validUrl = await validateImageUrl(imageUrl, {
          fallbackUrl,
          timeoutMs,
          retries,
          signal
        });
        
        // Only update state if not aborted
        if (!signal.aborted) {
          setValidatedImageUrl(validUrl);
          setHasError(validUrl === fallbackUrl && imageUrl !== fallbackUrl);
          setIsLoading(false);
        }
      } catch (error) {
        // Only update state if not aborted
        if (!signal.aborted) {
          setValidatedImageUrl(fallbackUrl);
          setHasError(true);
          setIsLoading(false);
          
          // Log error for monitoring (optional)
          if (process.env.NODE_ENV === 'development') {
            console.error('[useImageWithFallback] Error loading image:', error);
          }
        }
      }
    };

    loadImage();

    // Cleanup function: abort ongoing operations
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [imageUrl, fallbackUrl, timeoutMs, retries]);

  return {
    imageUrl: validatedImageUrl,
    isLoading,
    hasError
  };
};